// app/customs/playervoice/index.tsx
'use client';

import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import * as AuthContext from '@/utils/authcontext';
import * as Global from '@utils/global';
import './index.css';

type PlayerVoiceProps = {
  // employeeGUID: string;
  text: string;
  voiceType: string;
  speed?: number;
};

const PlayerVoice: React.FC<PlayerVoiceProps> = ({
  // employeeGUID,
  text,
  voiceType,
  speed = 1.0,
}) => {
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const { employeeGUID, employeeDomain } = AuthContext.useAuth();


  // ▶ audio イベントで状態同期（時間・再生フラグ）
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      if (!isNaN(audio.duration)) setDuration(audio.duration);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);


  // ▶ 元の挙動に近い形で「音声合成 → 自動再生」をまとめる
  const synthesizeAndPlay = async () => {
    if (!String(text ?? "").trim()) {
      alert("テキストが未入力です。");
      return;
    }
    if (isSynthesizing) return; // 連打防止

    setIsSynthesizing(true);
    try {
      const res = await axios.post<any>(
        `${Global.getApiBaseUrl()}/voice_talking/talk_by_type`,
        {
          // 社員GUID: employeeGUID,
          // text,
          // voicetype: voiceType,
          // speed,

          base_interface: {
            user_guid: employeeGUID,
            user_domain: employeeDomain,
            ip_address: window.location.hostname,
            user_agent: navigator.userAgent,
          },
          body_data: {
            text,
            voicetype: voiceType,
            speed,
          }

        },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);

      // DOM 更新を待ってから安全に再生
      setTimeout(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.src = url; // 念のため明示
        audio
          .play()
          .then(() => setIsPlaying(true))
          .catch(err => console.warn("再生失敗:", err));
      }, 100);
    } catch (err) {
      console.error("音声合成失敗:", err);
    } finally {
      setIsSynthesizing(false);
    }
  };

  // ▶ 再生／一時停止トグル
  const handlePlayPause = async () => {
    const audio = audioRef.current;

    // まだ音声生成していない場合は生成から
    if (!audioUrl) {
      await synthesizeAndPlay();
      return;
    }
    if (!audio) return;

    if (audio.paused) {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(err => console.warn("再生失敗:", err));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  // ▶ 5秒戻し／送り
  const handleSeek = (offset: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const maxDur = duration || audio.duration || 0;
    let newTime = audio.currentTime + offset;

    if (maxDur > 0) {
      newTime = Math.max(0, Math.min(newTime, maxDur));
    }

    audio.currentTime = newTime;
    setCurrentTime(audio.currentTime);
  };

  const changeSpeed = (delta: number) => {
    setPlaybackRate(prev => {
      const next = Math.max(0.5, Math.min(prev + delta, 3.0));
      if (audioRef.current) {
        audioRef.current.playbackRate = next;
      }
      return next;
    });
  };

  return (
    <div className="voiceplayer">
      <button
        type="button"
        onClick={handlePlayPause}
        disabled={isSynthesizing}
      >
        {
        isPlaying ? 
        (
          <i className="fas fa-pause"></i>
        ) : (
          <i className="fas fa-play"></i>
        )
        }
      </button>

      <button
        type="button"
        onClick={() => handleSeek(-5)}
        disabled={!audioUrl || isSynthesizing || !duration}
      >
        <i className="fas fa-step-backward"></i>
      </button>
      <button
        type="button"
        onClick={() => handleSeek(5)}
        disabled={!audioUrl || isSynthesizing || !duration}
      >
        <i className="fas fa-step-forward"></i>
      </button>

      {/* ← ここをまとめて1ブロックにする */}
      <div className="pv-box" >
        <div className="pv-status">
          {isSynthesizing ? (
            <>
              <span className="pv-spinner" />
              <span className="pv-loading-label">音声を生成中…</span>
            </>
          ) : (
            <span className="time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          )}
        </div>
      </div>


      <div className="pv-speed">
        <button
          type="button"
          onClick={() => changeSpeed(-0.25)}
          title="再生速度を下げる"
          disabled={!audioUrl || isSynthesizing || !duration}
        >
          <i className="fas fa-backward"></i>
        </button>

        <div className="rate-box">
          {playbackRate.toFixed(2)}×
        </div>

        <button
          type="button"
          onClick={() => changeSpeed(0.25)}
          title="再生速度を上げる"
          disabled={!audioUrl || isSynthesizing || !duration}
        >
          <i className="fas fa-forward"></i>
        </button>
      </div>


      {/* ▼ ここを追加 */}
      <button
        type="button"
        // className="pv-download"
        disabled={!audioUrl || isSynthesizing || !duration}
        onClick={() => {
          if (!audioUrl) return;
          const link = document.createElement("a");
          link.href = audioUrl;
          link.download = "voice.mp3";
          link.click();
        }}
      >
        <i className="fas fa-download"></i>
      </button>


      <audio ref={audioRef} src={audioUrl} />
    </div>
  );

};

const formatTime = (sec: number) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default PlayerVoice;
